import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Sparkles, PlayCircle, Compass, ArrowRight, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";

const KEY = "globo:onboarding-seen";

export function OnboardingModal() {
  const [open, setOpen] = useState(false);
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!user) return;
    if (localStorage.getItem(KEY)) return;
    // Only trigger on the dashboard route (first authenticated screen)
    if (location.pathname !== "/dashboard") return;
    const timer = setTimeout(() => setOpen(true), 600);
    return () => clearTimeout(timer);
  }, [user, location.pathname]);

  const close = () => {
    localStorage.setItem(KEY, "1");
    setOpen(false);
  };
  const goto = (path) => {
    close();
    navigate(path);
  };

  const steps = [
    {
      title: t("onb.step1.title"),
      body: t("onb.step1.body"),
    },
    {
      title: t("onb.step2.title"),
      body: t("onb.step2.body"),
    },
    {
      title: t("onb.step3.title"),
      body: t("onb.step3.body"),
    },
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
      <DialogContent className="sm:max-w-lg p-0 overflow-hidden" data-testid="onboarding-modal">
        <div className="relative bg-gradient-to-br from-primary/10 via-transparent to-accent/10 p-8">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-3 py-1 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">
            <Sparkles className="w-3 h-3 text-primary" /> {t("common.brand")}
          </div>
          <DialogHeader>
            <DialogTitle className="font-display font-extrabold text-3xl tracking-tight">
              {steps[step].title}
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground mt-3 leading-relaxed">
              {steps[step].body}
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center justify-between mt-8">
            <div className="flex gap-1.5">
              {steps.map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-full transition-all ${
                    i === step ? "w-8 bg-primary" : "w-1.5 bg-muted"
                  }`}
                />
              ))}
            </div>
            <div className="flex gap-2">
              {step < steps.length - 1 ? (
                <Button size="sm" onClick={() => setStep(step + 1)} data-testid="onb-next-btn">
                  {t("onb.next")} <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button size="sm" onClick={close} data-testid="onb-done-btn">
                  {t("onb.done")}
                </Button>
              )}
            </div>
          </div>
        </div>

        {step === steps.length - 1 && (
          <div className="grid sm:grid-cols-3 gap-3 p-6 pt-0">
            <button
              className="p-4 rounded-xl border border-border bg-card hover:border-primary hover:bg-primary/5 transition-colors text-left group"
              onClick={() => goto("/tutorial")}
              data-testid="onb-cta-tutorial"
            >
              <Compass className="w-5 h-5 text-primary mb-2" />
              <div className="font-display font-bold text-sm">{t("onb.cta.discover")}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{t("onb.cta.discoverBody")}</div>
            </button>
            <button
              className="p-4 rounded-xl border border-border bg-card hover:border-primary hover:bg-primary/5 transition-colors text-left"
              onClick={() => goto("/demo")}
              data-testid="onb-cta-demo"
            >
              <PlayCircle className="w-5 h-5 text-primary mb-2" />
              <div className="font-display font-bold text-sm">{t("onb.cta.demo")}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{t("onb.cta.demoBody")}</div>
            </button>
            <button
              className="p-4 rounded-xl border border-primary bg-primary text-primary-foreground hover:opacity-95 transition-opacity text-left"
              onClick={() => goto("/play")}
              data-testid="onb-cta-play"
            >
              <Sparkles className="w-5 h-5 mb-2" />
              <div className="font-display font-bold text-sm">{t("onb.cta.play")}</div>
              <div className="text-xs opacity-80 mt-0.5">{t("onb.cta.playBody")}</div>
            </button>
          </div>
        )}

        <button
          onClick={close}
          className="absolute top-3 right-3 text-muted-foreground hover:text-foreground"
          aria-label="Close"
          data-testid="onb-close-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </DialogContent>
    </Dialog>
  );
}
